import React, { useState, useEffect, useMemo } from 'react';
import { useOrganization, useAuth } from '@clerk/clerk-react';
import { Navigate } from 'react-router-dom';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ClipLoader from 'react-spinners/ClipLoader';
import { useFetch } from './api';
import SettingsHeader from './SettingsHeader';

const dataGridSx = {
  border: 'none',
  height: '100%',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#FAFAF9',
    borderBottom: '1px solid #E7E5E4',
    fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
    fontWeight: 500,
    fontSize: '0.8125rem',
    color: '#78716C',
  },
  '& .MuiDataGrid-columnSeparator': { display: 'none' },
  '& .MuiDataGrid-cell': {
    fontSize: '0.8125rem',
    fontWeight: 400,
    fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
    color: '#44403C',
    borderBottom: '1px solid #F5F5F4',
  },
  '& .MuiDataGrid-row:hover': { backgroundColor: '#FAFAF9' },
  '& .MuiDataGrid-footerContainer': { borderTop: '1px solid #F5F5F4' },
};

// billing is only supported from June 2026 forward — earlier months have unreliable
// churn timestamps, so we don't offer them
function buildMonthOptions() {
  const start = new Date(2026, 5, 1);
  const now = new Date();
  const options = [];
  const cursor = new Date(start);
  while (cursor <= now) {
    const year = cursor.getFullYear();
    const month = String(cursor.getMonth() + 1).padStart(2, '0');
    const label = cursor.toLocaleString([], { month: 'long', year: 'numeric' });
    // send the first of the month; the endpoint takes any date in the month
    options.push({ value: `${year}-${month}-01`, label });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return options.reverse();
}

function currentMonthValue() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${month}-01`;
}

function monthLabel(value) {
  const [year, month] = value.split('-');
  return new Date(Number(year), Number(month) - 1, 1)
    .toLocaleString([], { month: 'long', year: 'numeric' });
}

function toCsv(columns, rows) {
  const escape = (value) => {
    const str = value == null ? '' : String(value);
    if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
    return str;
  };
  const lines = [columns.map((col) => escape(col.headerName)).join(',')];
  rows.forEach((row) => {
    lines.push(columns.map((col) => {
      const value = col.csvValue ? col.csvValue(row) : row[col.field];
      return escape(value);
    }).join(','));
  });
  return lines.join('\n');
}

function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString([], {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

const TOTAL_TIP = 'Reflects the number of billable connections in the invoice period. In each month, '
  + 'billable connections are the sum of 1) active connections, 2) connections deleted in the given '
  + 'month, and 3) connections disabled in the given month. A connection falls into only one of these '
  + 'groups, so none is counted twice.\n\nTrackstar sandbox connections are not billable.';

const BUCKETS = [
  {
    key: 'active',
    label: 'Active',
    dot: 'bg-success',
    tip: 'Reflects all connections that exist and are not deleted or disabled. For prior months, this '
      + 'is the count as of the last day of the month; for the current period, it reflects active '
      + 'connections right now.',
  },
  {
    key: 'deleted',
    label: 'Deleted',
    dot: 'bg-error',
    tip: 'Reflects connections deleted during a given month that were created in a prior month. '
      + 'Deleted connections are billable in the month in which they were deleted but are no longer '
      + 'billable in subsequent months. Trackstar does not bill for a connection created and deleted '
      + 'in the same month.',
  },
  {
    key: 'paused',
    label: 'Disabled',
    dot: 'bg-warning',
    tip: 'Reflects connections disabled during a given month that were created in a prior month. '
      + 'Disabled connections are billable in the month in which they were disabled but are no longer '
      + 'billable in subsequent months. If a disabled connection is re-enabled, it becomes billable '
      + 'again in the month it is re-enabled. Trackstar does not bill for a connection created and '
      + 'disabled in the same month.',
  },
];

const BUCKET_META = {
  total: { label: 'All billable', dot: 'bg-accent-500', empty: 'billable' },
  active: { label: 'Active', dot: 'bg-success', empty: 'active' },
  deleted: { label: 'Deleted', dot: 'bg-error', empty: 'deleted' },
  paused: { label: 'Disabled', dot: 'bg-warning', empty: 'disabled' },
};

const STATUS_BADGE = {
  active: 'badge-success',
  deleted: 'badge-error',
  paused: 'badge-warning',
};

const STATUS_LABEL = {
  active: 'Active',
  deleted: 'Deleted',
  paused: 'Disabled',
};

// fixed widths so every table lines up identically — it's the same underlying data
const connectionIdColumn = {
  field: 'connection_id',
  headerName: 'Connection ID',
  width: 300,
  renderCell: (params) => (
    <span className="font-mono text-xs text-surface-600">{params.value}</span>
  ),
};

const integrationColumn = {
  field: 'integration',
  headerName: 'Integration',
  flex: 1,
  minWidth: 150,
  renderCell: (params) => (
    <span className="capitalize text-surface-800">{params.value}</span>
  ),
};

const typeColumn = {
  field: 'type',
  headerName: 'Type',
  width: 90,
  renderCell: (params) => (
    <span className="badge badge-neutral uppercase text-[0.6875rem]">{params.value}</span>
  ),
};

const customerIdColumn = {
  field: 'customer_id',
  headerName: 'Customer ID',
  width: 180,
  renderCell: (params) => (
    <span className="font-mono text-xs text-surface-600">{params.value || '—'}</span>
  ),
};

const statusColumn = {
  field: 'status',
  headerName: 'Status',
  width: 110,
  csvValue: (row) => STATUS_LABEL[row.status],
  renderCell: (params) => (
    <span className={`badge ${STATUS_BADGE[params.value]}`}>{STATUS_LABEL[params.value]}</span>
  ),
};

function dateColumn(field, headerName) {
  return {
    field,
    headerName,
    width: 140,
    renderCell: (params) => (
      <span className="text-xs text-surface-600">{formatDate(params.value)}</span>
    ),
  };
}

// every table shares the same columns; only the trailing churn column's label/field changes
function columnsFor(bucketKey) {
  const churnColumn = {
    deleted: dateColumn('deleted_at', 'Deleted'),
    paused: dateColumn('paused_at', 'Disabled'),
  }[bucketKey] || dateColumn('changed_at', 'Changed');
  return [
    connectionIdColumn,
    integrationColumn,
    typeColumn,
    statusColumn,
    customerIdColumn,
    dateColumn('created_at', 'Created'),
    churnColumn,
  ];
}

function StatCard({
  label, count, dot, tip, selected, accent, onClick,
}) {
  return (
    <Tooltip
      title={tip}
      arrow
      placement="top"
      slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 320 } } }}
    >
      <button
        type="button"
        onClick={onClick}
        className={`stat-card text-left cursor-pointer ${
          accent ? 'bg-gradient-to-br from-accent-50 to-surface-0 ' : ''
        }${selected ? 'ring-2 ring-inset ring-accent-400 border-accent-300' : ''}`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${dot}`} />
            <span className="text-xs font-medium uppercase tracking-wide text-surface-500">
              {label}
            </span>
          </div>
          <InfoOutlinedIcon sx={{ fontSize: 14, color: '#A8A29E' }} />
        </div>
        <p className="mt-2 text-3xl font-semibold text-surface-900 tabular-nums">{count}</p>
      </button>
    </Tooltip>
  );
}

function UsageBilling() {
  const { organization } = useOrganization();
  const { orgRole } = useAuth();
  const trackstar = useFetch();

  const [monthOptions] = useState(buildMonthOptions);
  const [selectedMonth, setSelectedMonth] = useState(currentMonthValue);
  const [buckets, setBuckets] = useState({ active: [], deleted: [], paused: [] });
  const [selectedBucket, setSelectedBucket] = useState('total');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setSearch('');
    // don't fetch for non-admins — the component redirects, but the effect still
    // runs after the first paint, which would fire the request before the redirect
    if (orgRole !== 'admin') return undefined;
    if (!organization) return undefined;
    setLoading(true);
    setError(null);
    let cancelled = false;
    trackstar(`internal/billing/billable-connections?period=${selectedMonth}`)
      .then((response) => {
        if (cancelled) return;
        setBuckets(response.data);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load billable connections', err);
        setError('Could not load billing data. Please try again.');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedMonth, organization, orgRole]);

  const rows = useMemo(() => {
    // tag every row with its bucket + a unified churn date so all tables share columns
    const tag = (row, status) => ({
      ...row,
      status,
      changed_at: row.deleted_at || row.paused_at || null,
    });
    if (selectedBucket === 'total') {
      const combined = [];
      BUCKETS.forEach((bucket) => {
        buckets[bucket.key].forEach((row) => combined.push(tag(row, bucket.key)));
      });
      return combined;
    }
    return (buckets[selectedBucket] || []).map((row) => tag(row, selectedBucket));
  }, [buckets, selectedBucket]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((row) => (
      String(row.connection_id).toLowerCase().includes(q)
      || String(row.integration).toLowerCase().includes(q)
      || String(row.type).toLowerCase().includes(q)
      || String(row.customer_id || '').toLowerCase().includes(q)
    ));
  }, [rows, search]);

  if (orgRole !== 'admin') {
    return <Navigate replace to="/settings" />;
  }

  const isCurrentMonth = selectedMonth === currentMonthValue();
  const total = buckets.active.length + buckets.deleted.length + buckets.paused.length;
  const snapshot = isCurrentMonth
    ? 'as of right now'
    : `as of the end of ${monthLabel(selectedMonth)}`;
  const meta = BUCKET_META[selectedBucket];

  const handleDownload = () => {
    const csv = toCsv(columnsFor(selectedBucket), filteredRows);
    downloadCsv(`billable-connections-${selectedBucket}-${selectedMonth}.csv`, csv);
  };

  return (
    <div className="h-full flex flex-col gap-5">
      <SettingsHeader
        title="Usage & Billing"
        subtitle={`Billable connections ${snapshot}`}
        actions={(
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="border border-surface-200 rounded-lg px-3 py-2 text-sm font-medium text-surface-700 bg-white shadow-xs hover:border-surface-300"
          >
            {monthOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        )}
      />

      {error && (
        <div className="p-3 bg-error-light rounded-lg border border-red-200 flex-shrink-0">
          <p className="text-sm text-error-dark">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><ClipLoader color="#D4532A" size={28} /></div>
      ) : (
        <>
          {/* summary + breakdown cards (breakdown cards drive the table) */}
          <div className="grid grid-cols-4 gap-4 flex-shrink-0">
            <StatCard
              label="Total billable"
              count={total}
              dot="bg-accent-500"
              tip={TOTAL_TIP}
              accent
              selected={selectedBucket === 'total'}
              onClick={() => setSelectedBucket('total')}
            />
            {BUCKETS.map((bucket) => (
              <StatCard
                key={bucket.key}
                label={bucket.label}
                count={buckets[bucket.key].length}
                dot={bucket.dot}
                tip={bucket.tip}
                selected={selectedBucket === bucket.key}
                onClick={() => setSelectedBucket(bucket.key)}
              />
            ))}
          </div>

          {/* detail table for the selected bucket — the only thing that scrolls */}
          <div className="card flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="flex items-center justify-between gap-4 px-5 py-3.5 border-b border-surface-100 flex-shrink-0">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
                <span className="text-sm font-semibold text-surface-800">
                  {meta.label}
                  {' '}
                  connections
                </span>
                <span className="badge badge-neutral">{rows.length}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <SearchIcon
                    sx={{ fontSize: 16, color: '#A8A29E' }}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2"
                  />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search connections"
                    className="w-56 border border-surface-200 rounded-lg pl-8 pr-3 py-1.5 text-sm text-surface-700 bg-white focus:outline-none focus:border-surface-300"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleDownload}
                  disabled={rows.length === 0}
                  className="btn-secondary flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
                  CSV
                </button>
              </div>
            </div>
            {rows.length === 0 ? (
              <div className="p-12 text-center text-sm text-surface-500">
                No
                {' '}
                {meta.empty}
                {' '}
                connections this month
              </div>
            ) : (
              <div className="flex-1 min-h-0">
                <DataGridPro
                  key={selectedBucket}
                  rows={filteredRows}
                  columns={columnsFor(selectedBucket)}
                  getRowId={(row) => row.connection_id}
                  hideFooter
                  disableRowSelectionOnClick
                  rowHeight={46}
                  sx={dataGridSx}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default UsageBilling;
